// Page-transition layer: a full-screen canvas of big squares that fill / clear
// the screen in a randomised stagger from an origin point. Opaque and un-blended
// (the cursor trail canvas sits above it). Driven by page-transition.js.

const TCELL = 10; // px, size of one square
const SPREAD = 300; // ms, delay between the origin cell and the farthest one
const JITTER = 150; // ms, random extra delay per cell

export const DURATION = SPREAD + JITTER; // ms, full fill / reveal length
export const COLOR = "#222423"; // black_900

export default class PixelOverlay {
  static instance = null;

  static init() {
    PixelOverlay.instance = new PixelOverlay();
    return PixelOverlay.instance;
  }

  constructor() {
    this.raf = null;
    // mode: "none" | "fill" | "full" | "reveal"
    this.grid = { mode: "none", start: 0, delays: null, cols: 0, rows: 0, ox: 0, oy: 0, done: null };

    this.canvas = document.createElement("canvas");
    this.canvas.className = "pixel-overlay";
    this.canvas.setAttribute("aria-hidden", "true");
    document.body.appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d");

    this.tick = this.tick.bind(this);
    this.resize = this.resize.bind(this);
    this.resize();
    window.addEventListener("resize", this.resize);
  }

  resize() {
    // Square edges sit on whole CSS pixels, so DPR 1 stays crisp and is 4x cheaper on retina.
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    if (this.grid.mode !== "none") this.buildDelays(this.grid.ox, this.grid.oy);
    this.wake();
  }

  wake() {
    if (this.raf === null) this.raf = requestAnimationFrame(this.tick);
  }

  // Per-cell delay = distance from the origin (normalised) * SPREAD + random jitter.
  buildDelays(ox, oy) {
    const g = this.grid;
    g.ox = ox;
    g.oy = oy;
    g.cols = Math.ceil(window.innerWidth / TCELL);
    g.rows = Math.ceil(window.innerHeight / TCELL);
    const oc = ox / TCELL;
    const or = oy / TCELL;
    const far = Math.hypot(Math.max(oc, g.cols - oc), Math.max(or, g.rows - or)) || 1;
    g.delays = new Float32Array(g.cols * g.rows);
    for (let r = 0; r < g.rows; r++) {
      for (let c = 0; c < g.cols; c++) {
        const d = Math.hypot(c + 0.5 - oc, r + 0.5 - or) / far;
        g.delays[r * g.cols + c] = d * SPREAD + Math.random() * JITTER;
      }
    }
    g.duration = DURATION;
  }

  // Squares pop in from (x, y) until the screen is covered. Resolves when full.
  fill(x, y) {
    return this.run("fill", x, y);
  }

  // Screen starts fully covered (no animation); call reveal() to clear it.
  cover(x, y) {
    this.buildDelays(x, y);
    this.grid.mode = "full";
    // Paint now rather than on the next frame: a fresh canvas is only composited
    // once it has content, so the CSS stand-in must outlive this by a few frames.
    this.tick(performance.now());
  }

  // Squares disappear from (x, y) until the screen is clear. Resolves when clear.
  reveal(x = this.grid.ox, y = this.grid.oy) {
    return this.run("reveal", x, y);
  }

  clear() {
    this.grid.mode = "none";
    this.grid.done?.();
    this.grid.done = null;
    this.wake();
  }

  run(mode, x, y) {
    return new Promise((resolve) => {
      this.buildDelays(x, y);
      this.grid.mode = mode;
      this.grid.start = performance.now();
      this.grid.done = resolve;
      this.wake();
    });
  }

  drawGrid(now) {
    const g = this.grid;
    if (g.mode === "none") return false;

    const elapsed = now - g.start;
    const { ctx } = this;

    let busy = false;
    for (let r = 0; r < g.rows; r++) {
      for (let c = 0; c < g.cols; c++) {
        const delay = g.delays[r * g.cols + c];
        const on =
          g.mode === "full" ||
          (g.mode === "fill" && elapsed >= delay) ||
          (g.mode === "reveal" && elapsed < delay);
        if (on) ctx.fillRect(c * TCELL, r * TCELL, TCELL, TCELL);
      }
    }

    if (g.mode === "fill" || g.mode === "reveal") {
      if (elapsed >= g.duration) {
        const done = g.done;
        g.done = null;
        g.mode = g.mode === "fill" ? "full" : "none";
        done?.();
      } else {
        busy = true;
      }
    }
    return busy;
  }

  tick(now) {
    const { ctx } = this;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = COLOR;
    this.raf = this.drawGrid(now) ? requestAnimationFrame(this.tick) : null;
  }
}
